import * as React from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../../api";
import { Oops } from "../../../utilities/oops";
import { EMAIL_PATTERN, PASSWORD_MIN_LENGTH, PasswordResetProps, PasswordResetViewModel } from "./passwordreset.types";
import { LogData } from "@rogoag/shared-ts";
import { logger } from "../../../utilities/logger";

// oxlint-disable-next-line no-unused-vars
export function usePasswordResetViewModel(props: PasswordResetProps): PasswordResetViewModel {
	// Extract token from URL
	const queryString = window.location.hash.split("?")[1];
	const params = new URLSearchParams(queryString);
	const token = params.get("token") ?? "";

	// State
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [emailError, setEmailError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");
	const [error, setError] = React.useState("");
	const [isTokenValid, setIsTokenValid] = React.useState(false);
	const navigate = useNavigate();

	// Token validation effect
	React.useEffect(() => {
		async function checkToken() {
			let validatedEmail = "";
			try {
				validatedEmail = await API.Authentication.validateNewPasswordToken(token);
				const isValid = isValidToken(validatedEmail);
				setIsTokenValid(isValid);

				function isValidToken(validatedEmail: string) {
					const isEmptyToken = token === null || token === "";
					let isValid = false;
					if (isEmptyToken) {
						isValid = false;
					} else if (validatedEmail !== null && validatedEmail !== "" && !isEmptyToken) {
						setEmail(validatedEmail);
						isValid = true;
					} else {
						isValid = false;
					}
					return isValid;
				}
			} catch (error: any) {
				Oops.handleError(error, "Authentication.PasswordReset.checkToken", email || validatedEmail);
			}
		}
		checkToken();
	}, []);

	// Email change handler
	const handleEmailChange = (event: { target: { value: React.SetStateAction<string> } }) => {
		setEmailError("");
		// validate that username is an email with regex
		if (!EMAIL_PATTERN.test(event.target.value.toString())) {
			setEmailError("Invalid email address");
		}

		setEmail(event.target.value);
	};

	// Password change handler
	const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setPasswordError("");
		const password = event.target.value;
		if (password == "") {
			setPasswordError("Password cannot be empty");
			return;
		}

		if (password.length < PASSWORD_MIN_LENGTH) {
			setPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setPassword(password);
		if (password != confirmPassword) {
			setPasswordError("Passwords do not match");
			return;
		}
	};

	// Confirm password change handler
	const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setConfirmPasswordError("");
		const confirmPassword = event.target.value;
		if (confirmPassword == "") {
			setConfirmPasswordError("Password cannot be empty");
			return;
		}

		if (confirmPassword.length < PASSWORD_MIN_LENGTH) {
			setConfirmPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setConfirmPassword(confirmPassword);
		if (confirmPassword != password) {
			setConfirmPasswordError("Passwords do not match");
			return;
		}
	};

	// Password reset request handler
	const handlePasswordReset = async (event: React.ChangeEvent<HTMLFormElement>) => {
		const logData: LogData = {
			function: "passwordreset.viewmodel.handlePasswordReset",
			email: email,
		};
		event.preventDefault();
		setLoading(true);
		if (emailError) {
			setLoading(false);
			return;
		}
		setError("");

		try {
			logger.log("Sending password reset email", logData);
			await API.Authentication.resetPassword(email);
			alert("Check your email for a link to reset your password.");
			navigate("/login");
		} catch (error: any) {
			Oops.handleError(error, logData.function, email);
		} finally {
			setLoading(false);
		}
	};

	// Password update handler
	const handlePasswordUpdate = async (event: React.ChangeEvent<HTMLFormElement>) => {
		const logData: LogData = {
			function: "passwordreset.viewmodel.handlePasswordUpdate",
			email: email,
		};
		event.preventDefault();
		setLoading(true);
		if (emailError) {
			setLoading(false);
			return;
		}
		setError("");

		try {
			logger.log("Attempting password update", logData);
			if (await API.Authentication.updatePassword(email, password, token)) {
				logger.log("Password update successful", logData);
				alert("Your password has been updated.");
			} else {
				throw Error("Password update failed");
			}
			navigate("/login");
		} catch (error: any) {
			Oops.handleError(error, logData.function, email);
		} finally {
			setLoading(false);
		}
	};

	return {
		email,
		password,
		confirmPassword,
		loading,
		emailError,
		passwordError,
		confirmPasswordError,
		error,
		isTokenValid,
		token,
		handleEmailChange,
		handlePasswordChange,
		handleConfirmPasswordChange,
		handlePasswordReset,
		handlePasswordUpdate,
	};
}
